// 账号设置 —— 通用面板，可在 Modal 或独立路由页里复用。
//
// 包含 4 段:
//   1. 账号信息 (头像 + 昵称 inline 编辑 + 邮箱 + 用户 ID)
//   2. 修改密码 (inline 表单)
//   3. 本机签名设备 (F2 pubkey 列表)
//   4. 退出登录
//   底部: 版本号 + 构建时间

import { useEffect, useRef, useState } from "react";

import { AdapterConfigSection } from "./AdapterConfigSection";
import { Avatar } from "./Avatar";
import { useDialog } from "./Dialog";
import {
  AuthApiError,
  checkUsernameAvailable,
  deleteMyAvatar,
  listMyPubkeys,
  setMyUsername,
  setPassword as apiSetPassword,
  updateMyProfile,
  uploadMyAvatar,
  validateUsernameClient,
  type MyPubkey,
} from "../lib/auth-api";
import { APP_VERSION, BUILD_TIME, GIT_COMMIT } from "../lib/build-info";
import { useI18n } from "../lib/i18n";
import { notifyRoomsChanged } from "../lib/room-bus";
import { getStorage, type BackupBundle } from "../lib/storage";
import { requestManualCheck } from "../lib/updater-bus";
import { fileToAvatarDataUrl } from "../lib/image";
import {
  BUILTIN_SOUNDS,
  clearCustomSound,
  getSoundPref,
  hasCustomSound,
  playSound,
  setCustomSoundDataUrl,
  setSoundPref,
  type SoundId,
} from "../lib/sounds";
import { useAuth } from "../lib/use-auth";

// UX-2026-05-27 第 2 波: 设置改单独页面 + 左侧 nav 5 tab.
// SettingsPanel 同时支持两种调用方式:
//   - Dialog 内嵌 (老路径): 不传 activeTab, 默认全部 section 都渲染
//   - 单独 /settings 页面: 传 activeTab, 只渲染该 tab 归属的 section
export type SettingsTab =
  | "all"
  | "account"
  | "security"
  | "ai"
  | "display"
  | "about";

interface SettingsPanelProps {
  /** 在 Modal 里时传 onClose, 右上 × 出现 + 操作完成不需跳转。
   *  独立页面用时不传, 退出登录由调用方处理 navigate。 */
  onClose?: () => void;
  /** 单独页面用时只显示该 tab 的 section. 不传 (或 "all") 显示全部. */
  activeTab?: SettingsTab;
}

// 各 section 归属哪个 tab — 给单独页面过滤用.
function show(tab: SettingsTab | undefined, owner: SettingsTab): boolean {
  return !tab || tab === "all" || tab === owner;
}

export function SettingsPanel({ onClose, activeTab }: SettingsPanelProps) {
  const { user, session, signOut, setUser } = useAuth();
  const { locale, setLocale, t } = useI18n();
  const dlg = useDialog();
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [checkingUpdate, setCheckingUpdate] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pubkeys, setPubkeys] = useState<MyPubkey[] | null>(null);
  const [keysErr, setKeysErr] = useState<string | null>(null);

  // 昵称 inline
  const [editingNick, setEditingNick] = useState(false);
  const [nickDraft, setNickDraft] = useState("");
  const [savingNick, setSavingNick] = useState(false);

  // 密码 inline
  const [editingPwd, setEditingPwd] = useState(false);
  const [pwd1, setPwd1] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  // K8: 通知铃声
  const [sound, setSound] = useState<SoundId>(() => getSoundPref());
  const [hasCustom, setHasCustom] = useState(() => hasCustomSound());
  const customInputRef = useRef<HTMLInputElement | null>(null);

  function pickSound(id: SoundId) {
    setSound(id);
    setSoundPref(id);
    void playSound(id);
  }

  function onCustomSoundFile(file: File) {
    if (!file.type.startsWith("audio/")) {
      void dlg.alert({ title: "格式不支持", message: "请上传 mp3 / wav / ogg 等音频" });
      return;
    }
    if (file.size > 512 * 1024) {
      void dlg.alert({ title: "文件太大", message: "请用 ≤ 512 KB 的短音 (1-3 秒)" });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const data = reader.result as string;
      setCustomSoundDataUrl(data);
      setHasCustom(true);
      pickSound("custom");
    };
    reader.readAsDataURL(file);
  }

  // 用户名 inline
  const [editingUname, setEditingUname] = useState(false);
  const [unameDraft, setUnameDraft] = useState("");
  const [unameStatus, setUnameStatus] = useState<
    { kind: "idle" } | { kind: "checking" } | { kind: "ok" } | { kind: "bad"; msg: string }
  >({ kind: "idle" });
  const [savingUname, setSavingUname] = useState(false);

  useEffect(() => {
    if (!editingUname) return;
    const draft = unameDraft.trim();
    const localErr = validateUsernameClient(draft);
    if (localErr) {
      setUnameStatus({ kind: "bad", msg: localErr });
      return;
    }
    if (draft === user?.username) {
      setUnameStatus({ kind: "idle" });
      return;
    }
    setUnameStatus({ kind: "checking" });
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await checkUsernameAvailable(draft);
        if (cancelled) return;
        if (r.available) setUnameStatus({ kind: "ok" });
        else setUnameStatus({ kind: "bad", msg: r.reason ?? "不可用" });
      } catch {
        if (!cancelled) setUnameStatus({ kind: "idle" });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [unameDraft, editingUname, user?.username]);

  useEffect(() => {
    if (!session) return;
    listMyPubkeys(session)
      .then((rows) => setPubkeys(rows))
      .catch((e) => setKeysErr(String(e)));
  }, [session]);

  if (!user) {
    return <div className="settings-panel"><p className="muted">{t("empty.signinRequired")}</p></div>;
  }

  async function saveNick() {
    if (!session || !user) return;
    setSavingNick(true);
    try {
      const next = await updateMyProfile(session, nickDraft.trim() || null);
      setUser(next);
      setEditingNick(false);
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.serverMessage : String(e);
      await dlg.alert({ title: "保存失败", message: msg });
    } finally {
      setSavingNick(false);
    }
  }

  async function savePwd() {
    if (!session) return;
    if (pwd1.length < 8) {
      await dlg.alert({ title: "密码太短", message: "至少 8 字符" });
      return;
    }
    if (pwd1 !== pwd2) {
      await dlg.alert({ title: "两次输入不一致", message: "请确认新密码后再保存" });
      return;
    }
    setSavingPwd(true);
    try {
      await apiSetPassword(session, pwd1);
      setEditingPwd(false);
      setPwd1("");
      setPwd2("");
      await dlg.alert({ title: "密码已更新", message: "下次登录请使用新密码。" });
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.serverMessage : String(e);
      await dlg.alert({ title: "保存失败", message: msg });
    } finally {
      setSavingPwd(false);
    }
  }

  async function saveUname() {
    if (!session) return;
    const draft = unameDraft.trim();
    const err = validateUsernameClient(draft);
    if (err) {
      setUnameStatus({ kind: "bad", msg: err });
      return;
    }
    setSavingUname(true);
    try {
      const next = await setMyUsername(session, draft);
      setUser(next);
      setEditingUname(false);
      setUnameStatus({ kind: "idle" });
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.serverMessage : String(e);
      await dlg.alert({ title: "保存失败", message: msg });
    } finally {
      setSavingUname(false);
    }
  }

  async function onPickAvatar(file: File) {
    if (!session) return;
    setSavingAvatar(true);
    try {
      const dataUrl = await fileToAvatarDataUrl(file);
      const next = await uploadMyAvatar(session, dataUrl);
      setUser(next);
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.serverMessage : String(e);
      await dlg.alert({ title: "上传失败", message: msg });
    } finally {
      setSavingAvatar(false);
    }
  }

  async function removeAvatar() {
    if (!session) return;
    const ok = await dlg.confirm({
      title: "移除头像",
      message: "确认移除？将回到首字母默认头像。",
      confirmLabel: "移除",
      variant: "danger",
    });
    if (!ok) return;
    setSavingAvatar(true);
    try {
      const next = await deleteMyAvatar(session);
      setUser(next);
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.serverMessage : String(e);
      await dlg.alert({ title: "移除失败", message: msg });
    } finally {
      setSavingAvatar(false);
    }
  }

  async function doSignOut() {
    const ok = await dlg.confirm({
      title: "退出登录",
      message: "确认退出当前账号？",
      confirmLabel: "退出",
      variant: "danger",
    });
    if (!ok) return;
    await signOut();
    onClose?.();
  }

  // C3: 导出全部本地数据为 JSON 文件
  async function doExportBackup() {
    try {
      const s = await getStorage();
      const bundle = await s.exportAll();
      const json = JSON.stringify(
        { ...bundle, app_version: APP_VERSION },
        null,
        2,
      );
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.href = url;
      a.download = `chatbox-backup-${ts}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      await dlg.alert({ title: "导出失败", message: String(e) });
    }
  }

  // C3: 导入 JSON 备份 (全量替换, 不可恢复)
  function doImportBackup() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json,.json";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const text = await file.text();
      let bundle: BackupBundle;
      try {
        bundle = JSON.parse(text) as BackupBundle;
      } catch {
        await dlg.alert({ title: "格式错误", message: "不是有效的 JSON 文件" });
        return;
      }
      if (!bundle.version || !Array.isArray(bundle.events) || !Array.isArray(bundle.rooms)) {
        await dlg.alert({
          title: "备份内容不完整",
          message: "缺 version / events / rooms 字段, 可能不是 ChatBox 备份",
        });
        return;
      }
      const ok = await dlg.confirm({
        title: "确认导入备份",
        message: `这会清空当前所有本地数据 (${bundle.events.length} 条消息, ${bundle.rooms.length} 个会话), 用备份覆盖. 操作不可恢复, 建议先点上方"导出"做一次新备份.`,
        confirmLabel: "继续导入",
        variant: "danger",
      });
      if (!ok) return;
      try {
        const s = await getStorage();
        await s.importAll(bundle);
        notifyRoomsChanged();
        await dlg.alert({
          title: "导入完成",
          message: `已恢复 ${bundle.events.length} 条消息, ${bundle.rooms.length} 个会话. 建议刷新页面 (⌘R) 让所有视图重新加载.`,
        });
      } catch (e) {
        await dlg.alert({ title: "导入失败", message: String(e) });
      }
    };
    input.click();
  }

  // D1: 手动检查更新, 触发 UpdaterRoot 的 dialog (即使用户之前 snooze 过)
  async function doCheckUpdate() {
    setCheckingUpdate(true);
    try {
      const result = await requestManualCheck();
      if (result === "no-update") {
        await dlg.alert({ title: "已是最新版本", message: `当前 v${APP_VERSION} 已是最新发布版本。` });
      } else if (result === "not-tauri") {
        await dlg.alert({
          title: "Web 端不支持自动更新",
          message: "刷新浏览器页面即可拿到最新前端。桌面端 (Tauri) 安装后可走自动更新。",
        });
      } else if (result === "error") {
        await dlg.alert({
          title: "检查更新失败",
          message: "网络或服务端异常, 请稍后再试。",
        });
      }
      // result === "found" 由 UpdaterRoot 自己弹窗, 这里啥都不做
    } finally {
      setCheckingUpdate(false);
    }
  }

  return (
    <div className="settings-panel">
      {onClose && (
        <header className="settings-panel__head">
          <h2>{t("nav.settings")}</h2>
          <button
            className="modal__close"
            onClick={onClose}
            aria-label={t("action.close")}
          >
            ×
          </button>
        </header>
      )}

      <div className="settings-panel__body">
        <div className="settings__head">
          <div className="settings__avatar-wrap">
            <Avatar
              seed={user.email}
              label={user.display_name ?? user.email}
              size={64}
              src={user.avatar_url}
            />
            <button
              type="button"
              className="settings__avatar-edit"
              onClick={() => fileInputRef.current?.click()}
              disabled={savingAvatar}
              title={t("settings.account.avatar")}
              aria-label={t("settings.account.avatar")}
            >
              {savingAvatar ? "…" : "✎"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onPickAvatar(f);
                e.target.value = ""; // 允许重选同一文件
              }}
            />
          </div>
          <div className="settings__head-text">
            <h1 className="settings__title">{user.display_name || t("settings.account.nick.unset")}</h1>
            <div className="settings__sub muted">{user.email}</div>
            <div className="settings__avatar-actions">
              <button
                type="button"
                className="link-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={savingAvatar}
              >
                {t("settings.account.avatar.upload")}
              </button>
              {user.avatar_url && (
                <button
                  type="button"
                  className="link-btn link-btn--danger"
                  onClick={() => void removeAvatar()}
                  disabled={savingAvatar}
                >
                  {t("settings.account.avatar.remove")}
                </button>
              )}
            </div>
          </div>
        </div>

        {show(activeTab, "account") && (
        <section className="settings__section">
          <h2>{t("settings.account.title")}</h2>
          <dl className="settings__kv">
            <dt>{t("settings.account.nickname")}</dt>
            <dd>
              {editingNick ? (
                <div className="settings__edit-row">
                  <input
                    className="input"
                    value={nickDraft}
                    maxLength={32}
                    autoFocus
                    placeholder={t("settings.account.nick.placeholder")}
                    onChange={(e) => setNickDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        void saveNick();
                      } else if (e.key === "Escape") {
                        setEditingNick(false);
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={savingNick}
                    onClick={() => void saveNick()}
                  >
                    {savingNick ? t("common.saving") : t("common.save")}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    disabled={savingNick}
                    onClick={() => setEditingNick(false)}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              ) : (
                <div className="settings__edit-row">
                  <span>{user.display_name || <span className="muted">{t("settings.account.nick.empty")}</span>}</span>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => {
                      setNickDraft(user.display_name ?? "");
                      setEditingNick(true);
                    }}
                  >
                    {t("common.edit")}
                  </button>
                </div>
              )}
            </dd>
            <dt>{t("settings.account.username")}</dt>
            <dd>
              {editingUname ? (
                <div>
                  <div className="settings__edit-row">
                    <input
                      className="input"
                      value={unameDraft}
                      maxLength={20}
                      autoFocus
                      placeholder={t("settings.account.uname.placeholder")}
                      onChange={(e) => setUnameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (unameStatus.kind === "ok") void saveUname();
                        } else if (e.key === "Escape") {
                          setEditingUname(false);
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn--primary"
                      disabled={savingUname || unameStatus.kind !== "ok"}
                      onClick={() => void saveUname()}
                    >
                      {savingUname ? t("common.saving") : t("common.save")}
                    </button>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      disabled={savingUname}
                      onClick={() => {
                        setEditingUname(false);
                        setUnameStatus({ kind: "idle" });
                      }}
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                  <div className={`settings__hint settings__hint--${unameStatus.kind}`}>
                    {unameStatus.kind === "checking" && t("settings.account.uname.checking")}
                    {unameStatus.kind === "ok" && t("settings.account.uname.ok")}
                    {unameStatus.kind === "bad" && unameStatus.msg}
                    {unameStatus.kind === "idle" && t("settings.account.uname.hint")}
                  </div>
                </div>
              ) : (
                <div className="settings__edit-row">
                  <span><code className="settings__uname">@{user.username ?? t("settings.account.uname.unset")}</code></span>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => {
                      setUnameDraft(user.username ?? "");
                      setUnameStatus({ kind: "idle" });
                      setEditingUname(true);
                    }}
                  >
                    {t("common.edit")}
                  </button>
                </div>
              )}
            </dd>
            <dt>{t("settings.account.email")}</dt>
            <dd>{user.email}</dd>
          </dl>
        </section>
        )}

        {show(activeTab, "security") && (
        <section className="settings__section">
          <h2>{t("settings.password.title")}</h2>
          {editingPwd ? (
            <div className="settings__pwd">
              <input
                className="input"
                type="password"
                placeholder={t("settings.password.placeholder1")}
                value={pwd1}
                autoFocus
                onChange={(e) => setPwd1(e.target.value)}
              />
              <input
                className="input"
                type="password"
                placeholder={t("settings.password.placeholder2")}
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void savePwd();
                  }
                }}
              />
              <div className="settings__edit-row">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={savingPwd}
                  onClick={() => void savePwd()}
                >
                  {savingPwd ? t("settings.password.saving") : t("settings.password.update")}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost"
                  disabled={savingPwd}
                  onClick={() => {
                    setEditingPwd(false);
                    setPwd1("");
                    setPwd2("");
                  }}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          ) : (
            <div className="settings__edit-row">
              <span className="muted">{t("settings.password.set")}</span>
              <button
                type="button"
                className="link-btn"
                onClick={() => setEditingPwd(true)}
              >
                {t("settings.password.modify")}
              </button>
            </div>
          )}
        </section>
        )}

        {show(activeTab, "ai") && (
        <section className="settings__section">
          <h2>{t("settings.ai.title")}</h2>
          <AdapterConfigSection />
        </section>
        )}

        {show(activeTab, "display") && (
        <section className="settings__section">
          <h2>{t("settings.display.lang.title")}</h2>
          <p className="muted small">{t("settings.display.lang.sub")}</p>
          <div className="settings__edit-row" style={{ marginTop: 10 }}>
            <label className="lang-radio">
              <input
                type="radio"
                checked={locale === "zh-CN"}
                onChange={() => setLocale("zh-CN")}
              />
              简体中文
            </label>
            <label className="lang-radio">
              <input
                type="radio"
                checked={locale === "en-US"}
                onChange={() => setLocale("en-US")}
              />
              English
            </label>
          </div>
        </section>
        )}

        {show(activeTab, "display") && (
        <section className="settings__section">
          <h2>{t("settings.display.sound.title")}</h2>
          <p className="muted small">
            新消息进来时播。每个频道可单独设静音 / 仅 @我 (Room 顶部 🔔)。
          </p>
          <ul className="sound-list">
            {BUILTIN_SOUNDS.map((s) => (
              <li
                key={s.id}
                className={
                  sound === s.id ? "sound-list__item is-active" : "sound-list__item"
                }
              >
                <label className="sound-list__row">
                  <input
                    type="radio"
                    name="sound"
                    checked={sound === s.id}
                    onChange={() => pickSound(s.id)}
                  />
                  <span className="sound-list__main">
                    <strong>{s.label}</strong>
                    <span className="muted small">{s.description}</span>
                  </span>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      void playSound(s.id);
                    }}
                  >
                    试听
                  </button>
                </label>
              </li>
            ))}
            {hasCustom && (
              <li
                className={
                  sound === "custom"
                    ? "sound-list__item is-active"
                    : "sound-list__item"
                }
              >
                <label className="sound-list__row">
                  <input
                    type="radio"
                    name="sound"
                    checked={sound === "custom"}
                    onChange={() => pickSound("custom")}
                  />
                  <span className="sound-list__main">
                    <strong>{t("settings.display.sound.custom")}</strong>
                    <span className="muted small">{t("settings.display.sound.custom.uploaded")}</span>
                  </span>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      void playSound("custom");
                    }}
                  >
                    试听
                  </button>
                  <button
                    type="button"
                    className="link-btn link-btn--danger"
                    onClick={(e) => {
                      e.preventDefault();
                      clearCustomSound();
                      setHasCustom(false);
                      if (sound === "custom") pickSound("ding");
                    }}
                  >
                    移除
                  </button>
                </label>
              </li>
            )}
          </ul>
          <div className="settings__edit-row" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="link-btn"
              onClick={() => customInputRef.current?.click()}
            >
              上传自定义铃声…
            </button>
            <input
              ref={customInputRef}
              type="file"
              accept="audio/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onCustomSoundFile(f);
                e.target.value = "";
              }}
            />
          </div>
        </section>
        )}

        {show(activeTab, "security") && (
        <section className="settings__section">
          <h2>{t("settings.security.devices.title")}</h2>
          <p className="muted small">
            每台设备一份 Ed25519 keypair, 私钥永不离开本机。agent invoke 走
            <code>confirm-each</code> 信任作用域时用本机 key 签名批准。
          </p>
          {keysErr && <p className="error">{keysErr}</p>}
          {!pubkeys && !keysErr && <p className="muted small">{t("settings.security.devices.loading")}</p>}
          {pubkeys && pubkeys.length === 0 && (
            <p className="muted small">{t("settings.security.devices.empty")}</p>
          )}
          {pubkeys && pubkeys.length > 0 && (
            <ul className="settings__list">
              {pubkeys.map((p) => (
                <li key={p.id}>
                  <div className="settings__list-main">
                    <span className="settings__device-label">
                      {p.device_label ?? t("settings.security.devices.unnamed")}
                    </span>
                    {p.revoked_at && <span className="badge badge--bad">{t("settings.security.devices.revoked")}</span>}
                  </div>
                  <code className="settings__device-key">{p.pubkey_b64.slice(0, 22)}…</code>
                  <div className="settings__list-meta muted small">
                    注册于 {new Date(p.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        )}

        {show(activeTab, "about") && (
        <section className="settings__section">
          <h2>{t("settings.about.title")}</h2>
          <dl className="settings__kv">
            <dt>{t("settings.about.version")}</dt>
            <dd>
              ChatBox v{APP_VERSION}
              <span className="muted small" style={{ marginLeft: 8 }}>
                build {BUILD_TIME} · {GIT_COMMIT}
              </span>
              <button
                type="button"
                className="link-btn"
                style={{ marginLeft: 12 }}
                disabled={checkingUpdate}
                onClick={() => void doCheckUpdate()}
              >
                {checkingUpdate ? t("settings.about.checkingUpdate") : t("settings.about.checkUpdate")}
              </button>
            </dd>
          </dl>
          <div className="settings__actions">
            <a className="link-btn" href="#/help">
              {t("settings.about.helpDocs")}
            </a>
            <a className="link-btn" href="https://github.com/colin3dmax/ChatBox/issues/new" target="_blank" rel="noreferrer">
              {t("settings.about.feedback")}
            </a>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => void doSignOut()}
            >
              {t("nav.logout")}
            </button>
          </div>

          {/* C3: 数据备份 / 恢复 */}
          <h3 style={{ marginTop: 24, fontSize: 14 }}>{t("settings.about.localData.subtitle")}</h3>
          <p className="muted small">{t("settings.about.localData.sub")}</p>
          <div className="settings__actions">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => void doExportBackup()}
            >
              {t("settings.about.export")}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => doImportBackup()}
            >
              {t("settings.about.import")}
            </button>
          </div>
          <p className="muted small">{t("settings.about.localData.warn")}</p>
        </section>
        )}

        {!activeTab && (
          // 兼容旧 Dialog 入口: 不传 activeTab 时底部留版本号
          <footer className="settings__foot">
            <span>ChatBox v{APP_VERSION}</span>
            <span className="muted">·</span>
            <span className="muted">build {BUILD_TIME}</span>
            <span className="muted">·</span>
            <span className="muted" title="git commit">{GIT_COMMIT}</span>
          </footer>
        )}
      </div>
    </div>
  );
}
